import { FALLBACK_BACKGROUND, rgbToHex, type TerminalMode } from './terminal-appearance';

/**
 * Resolve a CSS custom property (`--card`) to an opaque `#rrggbb`, or null.
 * Tokens may be `oklch(...)` and xterm's own parser takes neither that nor
 * `var()`, so the browser does the parsing: the computed colour is painted
 * into a 1×1 canvas and the pixel read back. Needs a DOM; the pure palette
 * logic lives in `terminal-appearance.ts`.
 *
 * `scope` is the element whose cascade the token is read in (the terminal's
 * own container), so a locally overridden token wins like it does on screen.
 */
export function resolveCssColorToken(scope: HTMLElement, token: string): string | null {
  const probe = document.createElement('div');
  probe.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;background-color:var(${token})`;
  scope.appendChild(probe);
  try {
    const computed = getComputedStyle(probe).backgroundColor;
    if (!computed || computed === 'rgba(0, 0, 0, 0)') return null; // token unset
    const ctx = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    // An unparsable value leaves fillStyle unchanged, so prime it with a
    // sentinel and detect that nothing took.
    ctx.fillStyle = '#010203';
    ctx.fillStyle = computed;
    if (ctx.fillStyle === '#010203') return null;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return rgbToHex(r, g, b);
  } catch {
    return null;
  } finally {
    probe.remove();
  }
}

/** The panel colour (`--card`) as an opaque hex; the per-mode fallback only when it cannot be resolved. */
export function resolveTerminalBackground(scope: HTMLElement, mode: TerminalMode): string {
  return resolveCssColorToken(scope, '--card') ?? FALLBACK_BACKGROUND[mode];
}

/** Eddy's monospace stack (`--font-mono`) as the browser computes it for `scope`, or null when unset. */
export function resolveCssFontStack(scope: HTMLElement): string | null {
  const value = getComputedStyle(scope).getPropertyValue('--font-mono').trim();
  return value || null;
}
